// src/components/TimeSelect.jsx
import React, { useMemo, useState, useRef, useEffect } from "react";
import Picker from "react-mobile-picker";

import down from "../icons/Arrow/down_tri.svg";
import { BottomWithButton } from "./BottomSheetGroup";
import { SplitRowQuarter } from "./ButtonGroup";

// 휠에 들어갈 값들 (01 ~ 12)
const HOUR_OPTIONS = Array.from({ length: 12 }, (_, i) =>
  String(i + 1).padStart(2, "0")
);
const MINUTE_OPTIONS = Array.from({ length: 60 }, (_, i) =>
  String(i).padStart(2, "0")
);
const PERIOD_OPTIONS = ["AM", "PM"];

// 현재 로컬(한국) 시간을 {hour, minute, period} 형태로 반환
const getCurrentLocalTimeState = () => {
  const now = new Date();            // 기기 로컬 시간 (한국 폰이면 KST)
  const hh = now.getHours();         // 0 ~ 23
  const mm = now.getMinutes();       // 0 ~ 59

  const isAm = hh < 12;
  let hour12 = hh % 12;
  if (hour12 === 0) hour12 = 12;

  return {
    hour: hour12,
    minute: mm,
    period: isAm ? "AM" : "PM",
  };
};

// "HH:MM"(24시간) -> { hour, minute, period("AM"/"PM") }
const parseValueToState = (value) => {
  // 👉 value가 없으면 현재시간으로 초기화
  if (!value) {
    return getCurrentLocalTimeState();
  }

  const [hhStr, mmStr] = value.split(":");
  let hh = Number(hhStr);
  let mm = Number(mmStr);

  if (Number.isNaN(hh) || hh < 0 || hh > 23) {
    // value가 이상하면 역시 현재시간으로 fallback
    return getCurrentLocalTimeState();
  }
  if (Number.isNaN(mm) || mm < 0 || mm > 59) mm = 0;

  const isAm = hh < 12;
  let hour12 = hh % 12;
  if (hour12 === 0) hour12 = 12;

  return {
    hour: hour12,
    minute: mm,
    period: isAm ? "AM" : "PM",
  };
};

// {hour12, minute, period} -> "HH:MM"(24시간)
const buildValueFromState = ({ hour, minute, period }) => {
  let hh = hour;
  if (period === "AM") {
    if (hour === 12) hh = 0;
  } else {
    if (hour !== 12) hh = hour + 12;
  }

  const hhStr = String(hh).padStart(2, "0");
  const mmStr = String(minute).padStart(2, "0");
  return `${hhStr}:${mmStr}`;
};

// "HH:MM" -> "오전 11:30"
const formatKoreanTimeLabel = (value) => {
  if (!value) return "";
  const [hhStr, mmStr] = value.split(":");
  const hh = Number(hhStr);
  const mm = Number(mmStr);
  const isAm = hh < 12;
  const period = isAm ? "오전" : "오후";
  let hour12 = hh % 12;
  if (hour12 === 0) hour12 = 12;
  return `${period} ${hour12}:${String(mm).padStart(2, "0")}`;
};

const TimeSelect = ({ value, onChange, placeholder = "시간을 선택해 주세요", hasError = false, }) => {
  const [open, setOpen] = useState(false);
  const [{ hour, minute, period }, setLocalTime] = useState(() =>
    parseValueToState(value)
  );

  // picker 영역 DOM 참조
  const scrollBlockRef = useRef(null);

  // 바텀시트가 열려 있을 때만 터치/휠 스크롤을 막는 네이티브 리스너 등록
  useEffect(() => {
    if (!open) return;
    const el = scrollBlockRef.current;
    if (!el) return;

    const handler = (e) => {
      // 여기서는 passive: false 로 등록했기 때문에 에러 없이 preventDefault 가능
      e.preventDefault();
    };

    el.addEventListener("touchmove", handler, { passive: false });
    el.addEventListener("wheel", handler, { passive: false });

    return () => {
      el.removeEventListener("touchmove", handler);
      el.removeEventListener("wheel", handler);
    };
  }, [open]);

  const selectedLabel = useMemo(
    () => (value ? formatKoreanTimeLabel(value) : ""),
    [value]
  );

  // 바텀시트 열 때, 외부 value 기준으로 로컬 상태 리셋
  const openSheet = () => {
    setLocalTime(parseValueToState(value));
    setOpen(true);
  };

  const handleConfirm = () => {
    const finalValue = buildValueFromState({ hour, minute, period });
    onChange?.(finalValue); // "HH:MM"으로 부모에게 전달
    setOpen(false);
  };

  // react-mobile-picker에 넘길 현재 선택값
  const pickerValue = {
    hour: String(hour).padStart(2, "0"),
    minute: String(minute).padStart(2, "0"),
    period,
  };


  return (
    <>
      {/* 셀렉트 버튼 */}
      <button
        type="button"
        onClick={openSheet}
        className="
          w-full h-[48px]
          rounded-[12px]
          border border-border-default
          bg-surface-light-white
          p-[12px] pl-[16px]
          flex items-center justify-between
          text-b1
        "
      >
        <span className={value ? hasError ? "text-text-danger" : "text-text-subtle" : "text-text-disabled"}>
          {value ? selectedLabel : placeholder}
        </span>
        <img src={down} alt="시간 선택" className="h-[24px]" />
      </button>

      {/* 바텀시트 + 휠 */}
      <BottomWithButton
        isOpen={open}
        onClose={() => setOpen(false)}
        height="h-[505px]"
        title="시간 선택"
        subtitle="알림 시간을 설정해 주세요."
        buttonDisabled={false}
        onSubmit={handleConfirm}
        renderButton={({ onClick, disabled }) => (
          <SplitRowQuarter
            leftText="닫기"
            leftType="empty"
            onLeft={() => setOpen(false)}
            rightText="저장"
            onRight={onClick}
            rightDisabled={disabled}
          />
        )}
      >
        {/* 휠 영역 (여기서 터치/휠의 기본 스크롤을 막음) */}
        <div
          ref={scrollBlockRef}
          className=""
          style={{ touchAction: "none" }}
        >
          <div className="relative w-full">
            {/* 가운데 선택 영역 가이드라인 (스크린샷의 얇은 선) */}
            <div
              className="
                pointer-events-none
                absolute inset-x-0
                top-1/2 -translate-y-1/2
                h-[64px]
                border-y border-border-default
              "
            />
            <Picker
              className="w-[220px] mx-auto"
              value={pickerValue}
              onChange={(nextValue) => {
                // nextValue: { hour: "05", minute: "00", period: "AM" }
                setLocalTime((prev) => {
                  const nextHourNum = Number(nextValue.hour);
                  const nextMinuteNum = Number(nextValue.minute);

                  return {
                    ...prev,
                    hour: Number.isNaN(nextHourNum) ? prev.hour : nextHourNum,
                    minute: Number.isNaN(nextMinuteNum) ? prev.minute : nextMinuteNum,
                    period: nextValue.period || prev.period,
                  };
                });
              }}
              height={320}
              itemHeight={64}
              wheelMode="natural"
            >
              {/* Hour 컬럼 */}
              <Picker.Column name="hour">
                {HOUR_OPTIONS.map((h) => (
                  <Picker.Item key={h} value={h}>
                    {({ selected }) => (
                      <div
                        className={[
                          "w-[64px] text-[24px] leading-[36px] text-center",
                          selected
                            ? "text-text-primary font-medium"
                            : "text-text-disabled",
                        ].join(" ")}
                      >
                        {h}
                      </div>
                    )}
                  </Picker.Item>
                ))}
              </Picker.Column>

              {/* 구분용 콜론 */}
              <Picker.Column name="colon" disabled>
                <Picker.Item value=":">
                  {() => (
                    <div className="text-[24px] text-center text-text-primary font-medium">
                      :
                    </div>
                  )}
                </Picker.Item>
              </Picker.Column>

              {/* Minute 컬럼 */}
              <Picker.Column name="minute">
                {MINUTE_OPTIONS.map((m) => (
                  <Picker.Item key={m} value={m}>
                    {({ selected }) => (
                      <div
                        className={[
                          "w-[64px] text-[24px] leading-[36px] text-center",
                          selected
                            ? "text-text-primary font-medium"
                            : "text-text-disabled",
                        ].join(" ")}
                      >
                        {m}
                      </div>
                    )}
                  </Picker.Item>
                ))}
              </Picker.Column>

              {/* AM / PM 컬럼 */}
              <Picker.Column name="period">
                {PERIOD_OPTIONS.map((p) => (
                  <Picker.Item key={p} value={p}>
                    {({ selected }) => (
                      <div
                        className={[
                          "w-[64px] text-[24px] leading-[36px] text-center",
                          selected
                            ? "text-text-primary font-medium"
                            : "text-text-disabled",
                        ].join(" ")}
                      >
                        {p}
                      </div>
                    )}
                  </Picker.Item>
                ))}
              </Picker.Column>
            </Picker>
          </div>
        </div>
      </BottomWithButton>
    </>
  );
};

export default TimeSelect;
